import { SidFormData } from 'common/types/sid-form-data';
import { useFormik } from 'formik';
import { AlertCircle } from 'lucide-react';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, InlineNotification, TextField, TextFieldClearButton } from '../../../../components';
import yup from '../../../../utils/validator';
import { editLvPersonCode } from '../../../../utils/lv-person-code.util';
import {InputAdornment, Stack} from '@mui/material';

interface SmartIdSubFormProps {
  authCountryCode: string;
  onSubmitSuccess: (value: SidFormData) => void;
  socketErrorCode: string;
  setSocketErrorCode: (value: string) => void;
  setIsDeviceLinkActive: Dispatch<SetStateAction<boolean>>;
  sidErrorOrigin?: 'qr' | 'personal' | null;
  continueButtonRef?: RefObject<HTMLButtonElement | null>;
}

export const SmartIdSubForm = ({
  authCountryCode,
  onSubmitSuccess,
  socketErrorCode,
  setSocketErrorCode,
  setIsDeviceLinkActive,
  sidErrorOrigin,
  continueButtonRef,
}: SmartIdSubFormProps) => {
  const { t } = useTranslation();
  const personCodeInputRef = useRef<HTMLInputElement>(null);
  const pendingSelection = useRef<{ value: string; start: number; end: number } | null>(null);

  const formik = useFormik({
    initialValues: {
      personCode: '',
    },
    validationSchema: yup.object().shape({
      personCode: authCountryCode === 'EE' ? yup.string().personalCodeEE() : yup.string().required(),
    }),
    validateOnChange: false,
    onSubmit: ({ personCode }) => {
      if (authCountryCode === 'LV' && !personCode.includes('-')) {
        personCode = personCode.replace(/(\d{6})(\d{5})/, '$1-$2');
      }

      onSubmitSuccess({
        personCode,
      });
    },
  });

  const socketErrorMessage = useMemo(() => {
    return socketErrorCode && socketErrorCode != 'INTERNAL_ERROR'
      ? t(`form.socketError.${socketErrorCode.toUpperCase()}.text`)
      : '';
  }, [socketErrorCode, t]);

  useEffect(() => {
    const screenreaderDiv = document.querySelector('output.screenreader-text');

    if (screenreaderDiv) {
      screenreaderDiv.innerHTML = socketErrorMessage;
    }
  }, [socketErrorMessage]);

  useEffect(() => {
    /* Move focus to the first field that has an error on form submit */
    const formikErrorKeys = Object.keys(formik.errors);
    if (formikErrorKeys.length > 0) {
      const elementId = formikErrorKeys[0];
      const element: HTMLInputElement | null = document.querySelector(`#${elementId}`);
      element?.focus();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.isSubmitting]);

  useEffect(() => {
    if (sidErrorOrigin === 'qr') return;
    setSocketErrorCode('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const input = personCodeInputRef.current;

    if (!input || authCountryCode !== 'LV') return;

    const captureSelection = () => {
      pendingSelection.current = {
        value: input.value,
        start: input.selectionStart ?? 0,
        end: input.selectionEnd ?? 0,
      };
    };

    input.addEventListener('beforeinput', captureSelection);

    return () => input.removeEventListener('beforeinput', captureSelection);
  }, [authCountryCode]);

  return (
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <Stack sx={{ gap: '1.5rem' }}>
            <TextField
                id="personCode"
                name="personCode"
                error={!!formik.errors.personCode}
                helperText={formik.errors.personCode ? t(formik.errors.personCode) : undefined}
                label={`${t('form.general.personCode')}`}
                value={formik.values.personCode}
                onChange={(e) => {
                  const value = e.target.value ?? '';

                  if (authCountryCode !== 'LV') {
                    formik.setFieldValue('personCode', value.trim());
                    return;
                  }

                  const caret = e.target.selectionStart ?? value.length;
                  const pending = pendingSelection.current;
                  pendingSelection.current = null;
                  const selection =
                    pending && pending.value === formik.values.personCode
                      ? { start: pending.start, end: pending.end }
                      : undefined;
                  const edit = editLvPersonCode(formik.values.personCode, value, caret, selection);

                  formik.setFieldValue('personCode', edit.personCode);
                  requestAnimationFrame(() => personCodeInputRef.current?.setSelectionRange(edit.caret, edit.caret));
                }}
                fullWidth
                inputRef={personCodeInputRef}
                slotProps={{
                  htmlInput: {
                    'data-testid': 'personCodeInput',
                    inputMode: 'numeric',
                    onFocus: () => setIsDeviceLinkActive(false),
                    onBlur: () => setIsDeviceLinkActive(true),
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Stack direction="row" sx={{ gap: 0.5 }}>
                          {formik.errors.personCode && (
                              <AlertCircle role="alert" color="#DE2700" size="1.5rem" />
                          )}
                          {formik.values.personCode && (
                              <TextFieldClearButton
                                  fieldName={t('form.general.personCode')}
                                  onClick={() => {
                                    formik.resetForm();
                                    personCodeInputRef.current?.focus();
                                  }}
                              />
                          )}
                        </Stack>
                      </InputAdornment>
                  ),
                  },
                }}
            />

            {socketErrorCode && socketErrorCode != 'INTERNAL_ERROR' &&
                sidErrorOrigin !== 'qr' && (
                <InlineNotification appearance="error">
                  <div {...{ 'data-testid': 'formError' }}>{socketErrorMessage}</div>
                </InlineNotification>
            )}

            <Button
                appearance="primary"
                size="m"
                type="submit"
                buttonRef={continueButtonRef}
                {...{ 'data-testid': 'continueBtn' }}
            >
              {t('form.general.continueBtn')}
            </Button>
          </Stack>
        </form>
  );
};
